import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { shortAddr } from "../../lib/api";

export const Hero = ({ config }) => {
  const [copied, setCopied] = useState(false);
  const addr = config?.token_address;

  const copy = async () => {
    if (!addr) return toast.info("Contract not deployed yet — see DEPLOYMENT.md");
    await navigator.clipboard.writeText(addr);
    setCopied(true);
    toast.success("Contract address copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="relative overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1707430393809-784967fe6fee?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-screen"
      />
      <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-5 gap-12 items-center">
        <div className="lg:col-span-3 fade-up">
          <p className="font-mono text-xs tracking-[0.2em] text-[#00C805] uppercase mb-6">
            Robinhood Chain · ERC-20 · Real ETH payouts
          </p>
          <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Hold <span className="text-[#00C805] font-bold">$BANK</span>.
            <br />
            Get paid <span className="font-bold">ETH</span>
            <br />
            every 30 minutes.
          </h1>
          <p className="mt-6 text-neutral-400 max-w-lg leading-relaxed text-base">
            The bank that pays you every 30 minutes. Every cycle, the vault's ETH is split
            across all holders — exactly pro-rata to your share of supply. No staking, no
            lockups, no claiming required.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              data-testid="copy-contract-btn"
              onClick={copy}
              className="group flex items-center gap-3 card-surface rounded-full px-5 py-3 hover:border-[#00C805]/40 transition-colors duration-200"
            >
              <span className="font-mono text-sm text-neutral-300" data-testid="contract-address">
                {addr ? shortAddr(addr) : "Contract: awaiting deployment"}
              </span>
              {copied ? <Check size={16} className="text-[#00C805]" /> : <Copy size={16} className="text-neutral-500 group-hover:text-[#00C805] transition-colors duration-200" />}
            </button>
            <div className="flex items-center gap-2">
              <a data-testid="social-twitter" href={config?.socials?.twitter || "https://x.com/hoodbank_cc"} target="_blank" rel="noreferrer" aria-label="Follow HoodBank on X" className="w-10 h-10 card-surface rounded-full flex items-center justify-center text-neutral-400 hover:text-[#00C805] transition-colors duration-200">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z"/></svg>
              </a>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="card-surface rounded-2xl p-8 border-t-2 border-t-[#00C805]">
            <p className="font-mono text-xs tracking-[0.2em] text-neutral-500 uppercase">Network</p>
            <p className="font-heading font-bold text-xl mt-1">Robinhood Chain</p>
            <div className="mt-6 space-y-4 font-mono text-sm">
              <div className="flex justify-between border-b border-white/5 pb-3">
                <span className="text-neutral-500">Chain ID</span>
                <span data-testid="hero-chain-id">4663</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-3">
                <span className="text-neutral-500">Gas / Rewards</span>
                <span className="text-[#00C805]">ETH</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-3">
                <span className="text-neutral-500">Payout cycle</span>
                <span>{config ? `${Math.round(config.cycle_seconds / 60)} min` : "30 min"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Explorer</span>
                <a data-testid="hero-explorer-link" href={config?.explorer_url} target="_blank" rel="noreferrer" className="text-[#00C805] hover:text-[#13D059] transition-colors duration-200">Blockscout ↗</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
